import React, { useState } from 'react';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REPORT_CATEGORIES = [
  {
    id: 'buffering', label: 'Mise en mémoire tampon et chargement',
    desc: 'La vidéo est floue ou il y a un problème de mise en mémoire tampon ou de chargement.',
    options: [
      'La vidéo met du temps à se charger ou se coupe régulièrement.',
      "La qualité de l'image est floue ou pixelisée.",
      'La lecture ne démarre pas du tout.',
    ],
  },
  {
    id: 'subtitles', label: 'Sous-titrage',
    desc: 'Les sous-titres ne fonctionnent pas correctement.',
    options: [
      'Les sous-titres contiennent des erreurs (orthographe, ponctuation, grammaire ou erreurs de traduction).',
      "Les sous-titres ne sont pas synchronisés avec l'audio.",
      "Les sous-titres ne s'affichent pas correctement.",
      'Les sous-titres ne sont pas disponibles dans la langue souhaitée.',
    ],
  },
  {
    id: 'audio_video', label: 'Audio et vidéo',
    desc: 'La vidéo est difficile à entendre ou à voir.',
    options: [
      "L'audio n'est pas synchronisé avec la vidéo.",
      "L'audio est manquant, ou le volume est trop faible, trop élevé ou trop fluctuant.",
      'Il y a un problème audio (saccades, déformations, interruptions).',
      "Certaines séquences de la vidéo n'apparaissent pas ou apparaissent bizarrement.",
      'Le doublage est inaudible ou de mauvaise qualité.',
    ],
  },
  {
    id: 'filters', label: 'Filtres de contenu',
    desc: "Un filtre ne fonctionne pas ou un filtre est manquant sur ce contenu.",
    options: [
      'Un filtre ne fonctionne pas (la scène aurait dû être filtrée mais ne l\'a pas été).',
      'Un filtre est manquant (une scène devrait être filtrée).',
      'Un filtre se déclenche au mauvais moment.',
    ],
    hasFilterFields: true,
  },
  {
    id: 'other', label: 'Autre problème',
    desc: 'Il y a un autre problème avec la série ou le film.',
    options: [
      'Cette série ou ce film contient des éléments inappropriés.',
      "La catégorie d'âge affichée est incorrecte.",
      'Les épisodes ou saisons sont dans le désordre.',
      'Le titre ou la description ne correspond pas ou contient des erreurs.',
      'Les images pour cette série ou ce film sont incorrectes, trompeuses ou contiennent des spoilers.',
    ],
  },
];

const fmtTc = (secs) => {
  const v = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(v / 3600), m = Math.floor((v % 3600) / 60), s = v % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function ReportProblemModal({ content, episode, timecode, profile, platform = 'web', tvNav = false, onClose }) {
  const [category, setCategory] = useState(null);
  const [checked, setChecked] = useState([]);
  const [details, setDetails] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTc, setFilterTc] = useState(fmtTc(timecode));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);

  const toggle = (opt) => setChecked(c => c.includes(opt) ? c.filter(o => o !== opt) : [...c, opt]);
  const canSend = checked.length > 0 || details.trim().length > 0;

  const send = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/reports`, {
        content_id: content?.id || '',
        content_title: content?.title || '',
        episode_id: episode?.id || '',
        timecode: Math.floor(timecode || 0),
        category: category.id,
        category_label: category.label,
        reasons: checked,
        details: details.trim(),
        filter_type: category.hasFilterFields ? filterType.trim() : '',
        filter_timecode: category.hasFilterFields ? filterTc.trim() : '',
        profile_id: profile?.id || '',
        profile_name: profile?.name || '',
        platform,
      }, { withCredentials: true });
      setSent(true);
      setTimeout(onClose, 2200);
    } catch {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-black/60 border border-white/20 rounded-md px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#E9641A] transition-colors";

  // Navigation télécommande TV (D-pad)
  React.useEffect(() => {
    if (!tvNav) return;
    const onKey = (e) => {
      const k = e.key;
      const isBack = ['Escape', 'Backspace', 'GoBack', 'XF86Back'].includes(k) || e.keyCode === 461 || e.keyCode === 10009;
      if (!['ArrowUp', 'ArrowDown', 'Enter'].includes(k) && !isBack) return;
      e.preventDefault(); e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (sent) return;
      if (!category) {
        if (k === 'ArrowDown') setFocusIdx(i => Math.min(REPORT_CATEGORIES.length - 1, i + 1));
        else if (k === 'ArrowUp') setFocusIdx(i => Math.max(0, i - 1));
        else if (k === 'Enter') { setCategory(REPORT_CATEGORIES[focusIdx]); setFocusIdx(0); }
        else onClose();
      } else {
        const sendIdx = category.options.length;
        if (k === 'ArrowDown') setFocusIdx(i => Math.min(sendIdx, i + 1));
        else if (k === 'ArrowUp') setFocusIdx(i => Math.max(0, i - 1));
        else if (k === 'Enter') { if (focusIdx < sendIdx) toggle(category.options[focusIdx]); else send(); }
        else { setCategory(null); setChecked([]); setFocusIdx(0); }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4" onClick={e => e.stopPropagation()} data-testid="report-modal">
      <div className="bg-[#181818] rounded-xl w-full max-w-xl max-h-[88vh] overflow-y-auto p-7 border border-white/10" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }}>
        {sent ? (
          <div className="flex flex-col items-center py-14 text-center" data-testid="report-sent">
            <span className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center mb-4"><Check size={26} className="text-green-400" /></span>
            <h3 className="text-white text-xl font-bold mb-2">Signalement envoyé</h3>
            <p className="text-white/50 text-sm">Merci ! Notre équipe va examiner le problème.</p>
          </div>
        ) : !category ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-2xl font-bold">Quel est le problème ?</h3>
              <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="report-close-btn"><X size={26} /></button>
            </div>
            <div className="space-y-2">
              {REPORT_CATEGORIES.map(c => (
                <button key={c.id} onClick={() => setCategory(c)} data-testid={`report-cat-${c.id}`}
                  className={`w-full text-left rounded-lg px-5 py-4 hover:bg-white/10 transition-colors group ${tvNav && focusIdx === REPORT_CATEGORIES.indexOf(c) ? 'bg-white/10 ring-2 ring-[#E9641A]' : ''}`}>
                  <span className="flex items-center justify-between">
                    <span>
                      <span className="block text-white text-lg font-bold mb-1">{c.label}</span>
                      <span className="block text-white/55 text-sm">{c.desc}</span>
                    </span>
                    <ChevronRight size={20} className="text-white/30 group-hover:text-white flex-none ml-3" />
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => { setCategory(null); setChecked([]); }} className="text-white/50 hover:text-white" data-testid="report-back-btn"><ChevronLeft size={26} /></button>
              <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="report-close-btn2"><X size={26} /></button>
            </div>
            <h3 className="text-white text-2xl font-bold mb-1">{category.label}</h3>
            <p className="text-white/70 text-sm font-semibold mb-5">Cochez toutes les réponses appropriées :</p>
            <div className="space-y-4 mb-6">
              {category.options.map((opt, i) => (
                <label key={i} className={`flex items-start gap-3 cursor-pointer group rounded-lg p-1.5 -m-1.5 ${tvNav && focusIdx === i ? 'bg-white/10 ring-2 ring-[#E9641A]' : ''}`} data-testid={`report-opt-${i}`}>
                  <span className={`w-6 h-6 rounded flex-none flex items-center justify-center border-2 transition-colors mt-0.5 ${checked.includes(opt) ? 'bg-[#E9641A] border-[#E9641A]' : 'border-white/40 bg-black group-hover:border-white'}`}>
                    {checked.includes(opt) && <Check size={15} className="text-white" />}
                  </span>
                  <input type="checkbox" className="hidden" checked={checked.includes(opt)} onChange={() => toggle(opt)} />
                  <span className="text-white/85 text-sm leading-relaxed">{opt}</span>
                </label>
              ))}
            </div>

            {category.hasFilterFields && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="report-filter-fields">
                <div>
                  <label className="block text-white/50 text-xs mb-1.5">Quel type de filtre ?</label>
                  <input value={filterType} onChange={e => setFilterType(e.target.value)} placeholder="Ex : violence, langage..." className={inputCls} data-testid="report-filter-type-input" />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5">À quel moment ? (timecode)</label>
                  <input value={filterTc} onChange={e => setFilterTc(e.target.value)} placeholder="HH:MM:SS" className={inputCls} data-testid="report-filter-tc-input" />
                </div>
              </div>
            )}

            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={4} maxLength={2000}
              placeholder="Votre problème n'est pas indiqué ? Vous avez d'autres détails ? Ajoutez-les ici. (Facultatif)"
              className={`${inputCls} resize-none mb-5`} data-testid="report-details-input" />

            <div className="flex justify-end">
              <button onClick={send} disabled={!canSend || sending} data-testid="report-send-btn"
                className={`px-8 py-3 rounded-md text-base font-bold transition-colors ${tvNav && category && focusIdx === category.options.length ? 'ring-2 ring-white ' : ''}${canSend ? 'bg-[#E9641A] hover:bg-[#FE975E] text-white' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}>
                {sending ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
